<template>
  <!-- Coleção sem relacionamento não tem de onde montar: o botão nem aparece -->
  <div v-if="relationships.length" class="rollup-builder">
    <v-btn v-if="!open" variant="text" size="small" prepend-icon="mdi-auto-fix" @click="open = true">
      {{ $tt('formula-rollup.open') }}
    </v-btn>
    <v-sheet v-else border rounded class="pa-3">
      <div class="text-caption text-medium-emphasis mb-2">{{ $tt('formula-rollup.hint') }}</div>
      <v-row dense align="center">
        <v-col cols="12" sm="4">
          <v-select :label="$tt('formula-rollup.relationship')" :items="relationships" v-model="relationshipAlias"
                    density="compact" hide-details @update:model-value="onRelationship"/>
        </v-col>
        <v-col cols="12" sm="4" v-if="relationship">
          <v-select :label="$tt('formula-rollup.field')" :items="fields" v-model="fieldAlias"
                    density="compact" hide-details :clearable="isList" @update:model-value="onField"/>
        </v-col>
        <!-- Agregação só quando a relação é uma LISTA: relação para um item só não tem o que
             agregar — é o caminho puro (`cliente.cidade`) -->
        <v-col cols="12" sm="4" v-if="relationship && isList">
          <v-select :label="$tt('formula-rollup.aggregation')" :items="aggregations" v-model="aggregation"
                    density="compact" hide-details/>
        </v-col>
      </v-row>
      <div class="d-flex justify-end mt-3">
        <v-btn variant="text" @click="open = false">{{ $tt('cancel') }}</v-btn>
        <v-btn color="primary" variant="flat" :disabled="!built" @click="apply">{{ $tt('formula-rollup.apply') }}</v-btn>
      </div>
    </v-sheet>
  </div>
</template>
<script lang="ts">
import {Component, Prop, Vue, toNative} from 'vue-facing-decorator'
import Idea from '@/types/Idea'
import IdeaProperty from '@/types/IdeaProperty'
import {EnhancedWorkpace} from '@/types/Workspace'
import {MessageKey} from '@/i18n'
import {travelsAsList} from '@/utils/propertyDefinition'
import {Aggregation, Rollup, rollupAggregations, rollupFields, rollupOf, suggestedAggregation} from '@/utils/rollup'

const AGGREGATION_LABELS: Record<Aggregation, MessageKey> = {
  count: 'formula-rollup.count', sum: 'formula-rollup.sum', avg: 'formula-rollup.avg',
  median: 'formula-rollup.median', min: 'formula-rollup.min', max: 'formula-rollup.max',
  all: 'formula-rollup.all'
}

/**
 * Um rollup sem escrever fórmula: a relação, o campo dos relacionados e — quando a relação é uma
 * lista — a agregação. O que o Notion chama de rollup.
 *
 * **É atalho de autoria, e não outro motor.** O resultado é uma fórmula comum, escrita no editor
 * (`itens.map(i, i.preco).sum()`), que se edita depois como qualquer outra; quem a confere e
 * calcula é o mesmo servidor. Junto vai o tipo que ela pede — contar dá número; "mostrar todos"
 * dá um múltiplo do tipo do campo, com o domínio dele quando é seleção —, que é a parte que quem
 * escreve à mão costuma errar.
 *
 * Aqui só as escolhas; a fórmula sai do `rollup.ts`, puro, que se confere fora da tela.
 */
@Component({
  name: 'ontos-rollup-builder',
  emits: ['apply']
})
class RollupBuilder extends Vue {
  @Prop({type: Object, required: true}) readonly idea!: Idea
  @Prop({type: Object, required: true}) readonly workspace!: EnhancedWorkpace

  open = false
  relationshipAlias: string | null = null
  fieldAlias: string | null = null
  aggregation: Aggregation | null = null

  get relationships() {
    return this.idea.properties
        .filter(property => property.relationship)
        .map(property => ({title: property.name, value: property.alias}))
  }

  get relationship(): IdeaProperty | undefined {
    return this.idea.properties.find(property => property.alias === this.relationshipAlias)
  }

  get isList(): boolean {
    return !!this.relationship && travelsAsList(this.relationship)
  }

  get target(): Idea | undefined {
    return this.relationship ? this.workspace.ideaWith(this.relationship.relationship!.idea) : undefined
  }

  get fields() {
    if (!this.relationship || !this.target) return []
    return rollupFields(this.relationship, this.target.properties)
        .map(property => ({title: property.name, value: property.alias}))
  }

  get field(): IdeaProperty | undefined {
    return this.target ? this.target.properties.find(property => property.alias === this.fieldAlias) : undefined
  }

  get aggregations() {
    return rollupAggregations(this.field)
        .map(aggregation => ({title: this.$tt(AGGREGATION_LABELS[aggregation]), value: aggregation}))
  }

  onRelationship() {
    this.fieldAlias = null
    this.aggregation = this.isList ? suggestedAggregation() : null
  }

  onField() {
    if (this.isList) this.aggregation = suggestedAggregation(this.field)
  }

  /** O atributo montado, ou `undefined` enquanto falta escolher alguma coisa. */
  get built(): Rollup | undefined {
    return this.relationship ? rollupOf(this.relationship, this.field, this.aggregation) : undefined
  }

  apply() {
    if (!this.built) return
    this.$emit('apply', this.built)
    this.open = false
  }
}

export default toNative(RollupBuilder)
</script>
